import type { MeDto } from "@clinic/shared";
import { useState } from "react";
import { useNavigate } from "react-router";
import { ApiError, api } from "../api";
import { fieldErrors, formValues, hasFieldErrors } from "../forms";
import { useT } from "../i18n";
import { useSessionMutation } from "../session";
import { AcceptTerms, withAcceptTerms } from "../site";
import { Alert, Button, Card, Field } from "../ui";
import { slugify } from "./public";

/** A signed-in user creates an additional clinic and becomes its owner (CB-23). */
export function NewClinicPage() {
  const { t, tError } = useT();
  const navigate = useNavigate();
  const [slug, setSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const create = useSessionMutation((input: Record<string, unknown>) => api<MeDto>("POST", "/clinics", input));
  const fields = fieldErrors(create.error);
  const code = create.error instanceof ApiError ? create.error.code : undefined;
  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">{t("clinics.new_title")}</h1>
      <Card>
        <p className="text-sm text-slate-600">{t("clinics.new_text")}</p>
        <form
          className="mt-4 space-y-4"
          onSubmit={(e) => create.mutate(withAcceptTerms(formValues(e)), { onSuccess: () => navigate("/app") })}
        >
          {create.error && !hasFieldErrors(create.error) && code !== "SLUG_TAKEN" ? (
            <Alert>{tError(create.error)}</Alert>
          ) : null}
          <Field
            label={t("auth.clinic_name")}
            name="name"
            required
            onChange={(e) => {
              if (!slugEdited) setSlug(slugify(e.target.value));
            }}
            error={fields.name}
          />
          <Field
            label={t("auth.booking_address")}
            name="slug"
            required
            value={slug}
            onChange={(e) => {
              setSlugEdited(true);
              setSlug(e.target.value);
            }}
            hint={slug ? t("auth.booking_address_preview", { url: `/book/${slug}` }) : t("auth.booking_address_hint")}
            error={fields.slug ?? (code === "SLUG_TAKEN" ? tError(create.error) : undefined)}
          />
          <AcceptTerms error={fields.acceptTerms} />
          <Button busy={create.isPending}>{t("clinics.new_button")}</Button>
        </form>
      </Card>
    </div>
  );
}
